import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch } from 'app/hooks';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmTabState } from 'common/hooks/usePalmTabState';
import { fetchClaims } from 'common/services/api/Claims/Claims.thunk';
import { ActionDefinition, ColumnDefinition, ColumnType, Operations } from 'common/types/TypeDefs';
import { claimTypeDropdownConfiguration } from 'common/utils/ClaimTypeDropdownFilterConfiguration';

import { ClaimDeleteDialogContent } from './ClaimDeleteDialogContent';
import { ClaimCreateContent } from './ClaimsEditDrawerContent';
import {
    addNewFilter,
    checkClaimTypeFilter,
    clearFilters,
    removeFilter,
    selectFilters,
} from './claimsSlice';

export function ClaimsTab() {
    const typeName = 'Claim';
    const { tenantId = '', suiteId = '' } = useParams();
    const dispatch = useAppDispatch();

    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager ]);
    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager, Roles.MultiTenantRO, Roles.UserManager ]);

    const dropdownProps = [{
        dropdownInfo: claimTypeDropdownConfiguration,
        dropdownReducer: (label: string) => dispatch(checkClaimTypeFilter(label)),
    }];

    const { processing, sortModel, setSortModel, filterProp, fetchEntities } = usePalmTabState({
        typeName,
        fetchParams: { tenantId },
        fetchThunk: fetchClaims,
        filterActions: { selectFilters, addNewFilter, removeFilter, clearFilters },
        dropdownProps,
    });

    const onGetRowLink = useCallback((claimId: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Claims}/${claimId}/${Routes.Users}`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: onGetRowLink },
        { action: Operations.Edit, disabled: shouldDisableButtons },
        { action: Operations.Delete, disabled: shouldDisableButtons }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'id', columnName: 'Claim ID', columnType: ColumnType.First, copy: true, shouldFilterByExactMatch: true },
        { columnId: 'name', columnName: 'Claim Name', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'claimDataTypeName', columnName: 'Claim Type', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <PalmDataGrid
        actionDefinitions={actionDefinitions}
        deleteFunction={ClaimDeleteDialogContent}
        columnDefinitions={columnDefinitions}
        title={typeName}
        processing={processing}
        isCreateButtonDisabled={shouldDisableButtons}
        filter={filterProp}
        contentFunction={ClaimCreateContent}
        getRowLink={shouldDisableOpenButton ? undefined : onGetRowLink}
        refreshDataGridClick={() => fetchEntities()}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
}
